// ============================================================================
// Historical Applications Types
// ============================================================================

export interface IApplicant {
    firstName?: string | null | undefined;
    lastName?: string | null | undefined;
    bewellMemberId?: string | null | undefined;
    seekingCoverage?: boolean;
    ssnLastFour?: string | null | undefined;
    dob?: string | null | undefined;
    csrLevel?: string | null | undefined;
    eligibleForAptc?: boolean;
    aptcAmount?: string | number | null | undefined;
    eligibleForNapa?: boolean;
    eligibleForQhp?: boolean;
    eligibleForStateSubsidy?: boolean;
    stateSubsidyAmount?: string | number | null | undefined;
}

export interface IApplication {
    applicationYear?: string;
    applicationId?: string;
    submissionDate?: string;
    primaryContactFirstName?: string;
    primaryContactLastName?: string;
    financial?: boolean;
    aptc?: string;
    stateSubsidy?: string;
    applicants?: IApplicant[];
    primaryTaxFilerNames: string[];
}

export interface IEligibilityResult {
    memberId: string;
    memberName: string;
    aptcAmount?: string | undefined | null;
    premiumAssistanceAmount?: string | undefined | null;
    hasCostSharingReductions: boolean;
}

export interface IHouseholdMember {
    name: string;
    memberId: string;
    seekingCoverage: string;
    primaryContact: string;
    ssn: string;
    dateOfBirth: string;
}

export interface IAmountDisplayProps {
    amount: string;
    labelId: string;
    tooltipContent: string;
    tooltipIndex: string;
}

export interface IHouseholdMembersSectionProps {
    application: IApplication;
}

// ============================================================================
// Enrollment Types
// ============================================================================

export enum EnrolleeType {
    SELF = 'Self(18)',
    SPOUSE = 'Spouse(01)'
}

export enum GenderCode {
    MALE = 'M',
    FEMALE = 'F',
}

export interface IAddress {
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: number | string | null;
    fipCode?: string | number | null;
}

export interface IEnrolleeHistoryEntry {
    firstName?: string;
    lastName?: string;
    coverageStartDate?: string;
    coverageEndDate?: string;
    relationship?: string;
    gender?: string;
    dob?: string;
    tobacco?: string;
    ssnLastFour?: string;
    memberId?: string;
}

export interface IEnrollment {
    policyId?: string | number | null;
    enrollmentStartDate?: string | null | undefined;
    enrollmentEndDate?: string | null | undefined;
    enrollmentStatus?: string | null | undefined;
    monthlyPremium?: string | null | undefined;
    netPremium?: string | null | undefined;
    electedAptc?: string | null | undefined;
    electedStateSubsidy?: string | null | undefined;
    appliedStateSubsidy2?: string | null | undefined;
    issuerName?: string | null | undefined;
    planName?: string | null | undefined;
    cmsPlanId?: string | null | undefined;
    primaryTaxFilerNames?: string[];
    homeAddress?: IAddress;
    mailingAddress?: IAddress;
    enrolleeHistoryEntries?: IEnrolleeHistoryEntry[];
}

export interface IEnrollee {
    type: string;
    name: string;
    gender: string;
    dateOfBirth: string;
    tobacco: string;
    ssn: string;
    coverageDates: string;
    memberId: string;
}

export interface IMonthlyPaymentSectionProps {
    id?: string;
    paymentItems?: IPaymentItem[];
    titleLabelId?: string;
}

export interface IPaymentItem {
    labelId: string;
    value: number | string | null | undefined;
    isTotal?: boolean;
    dataTestId?: string;
}

export interface IPlanCardSectionLabels {
    enrollmentStatusLabel?: string;
    effectiveDateLabel?: string;
}

export interface IPlanCardSectionProps {
    id?: string;
    enrollment: IEnrollment;
    labels?: IPlanCardSectionLabels;
    className?: string;
}

export interface IAdditionalInfoSectionProps {
    enrollment: IEnrollment;
}

export interface IAddressDetailsSectionProps {
    id?: string;
    homeAddress?: IAddress;
    mailingAddress?: IAddress;
    primaryTaxFilerNames: string[];
}

export interface IEnrolleesTableSectionProps {
    enrollment: IEnrollment;
}

export interface IEnrolleeCount {
    primary: number;
    spouse: number;
    dependent: number;
    [key: string]: number;
}
